import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    KeyRound, Copy, Check, ExternalLink, Trash2, ShieldAlert, Plus, X, Sparkles, Loader2, Rocket, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import SEO from "../components/SEO";

export default function Developers() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [newlyCreated, setNewlyCreated] = useState(null); // {name, full_key, prefix, daily_quota}

    useEffect(() => {
        if (authLoading) return; // wait for auth to hydrate
        if (!user) {
            navigate("/login?redirect=/developers");
            return;
        }
        loadKeys();
    }, [user, authLoading]); // eslint-disable-line

    async function loadKeys() {
        setLoading(true);
        try {
            const { data } = await api.get("/keys");
            setKeys(data);
        } catch (e) {
            toast.error("Could not load keys");
        } finally {
            setLoading(false);
        }
    }

    async function revoke(k) {
        if (!confirm(`Revoke "${k.name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/keys/${k.key_id}`);
            toast.success("Key revoked");
            loadKeys();
        } catch (e) {
            toast.error("Could not revoke key");
        }
    }

    return (
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
            <SEO
                title="Developer console — Manage your API keys"
                description="Create, revoke, and monitor your Real-Check API keys. Real-time usage tracking, per-key quota bars, and one-click integration."
                path="/developers"
                noindex
            />
            <div className="mb-10">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/15 px-3 py-1 text-[11px] font-semibold text-accent mb-4">
                    <KeyRound className="h-3 w-3 text-primary" /> Developer console
                </div>
                <h1 className="font-display text-4xl md:text-5xl text-accent tracking-tight leading-[1.05]">
                    Your API keys
                </h1>
                <p className="mt-3 text-lg text-muted-foreground max-w-2xl leading-relaxed">
                    Use Real-Check keys to power browser extensions, integrations, or your own tooling.
                    Each key gets its own daily quota, usage tracking, and one-click revoke.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                    <Button
                        className="rounded-full h-11 px-5 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                        onClick={() => setShowCreate(true)}
                        data-testid="dev-create-key-btn"
                    >
                        <Plus className="h-4 w-4 mr-2" /> Create new key
                    </Button>
                    <Link to="/docs/api">
                        <Button variant="secondary" className="rounded-full h-11 px-5" data-testid="dev-view-docs-btn">
                            <ExternalLink className="h-4 w-4 mr-2" /> API documentation
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Keys list */}
            {loading ? (
                <div className="text-center py-16 text-muted-foreground">
                    <Loader2 className="h-6 w-6 mx-auto animate-spin" />
                </div>
            ) : keys.length === 0 ? (
                <Card className="card-surface rounded-2xl p-10 text-center" data-testid="dev-empty-state">
                    <KeyRound className="h-10 w-10 mx-auto text-primary opacity-70 mb-4" />
                    <h3 className="font-display text-2xl text-accent mb-2">No keys yet</h3>
                    <p className="text-muted-foreground max-w-md mx-auto mb-6">
                        Create your first key to start hitting the Real-Check fact-check API from your extension or app.
                    </p>
                    <Button
                        className="rounded-full h-11 px-5 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                        onClick={() => setShowCreate(true)}
                    >
                        <Plus className="h-4 w-4 mr-2" /> Create your first key
                    </Button>
                </Card>
            ) : (
                <>
                    <QuotaUpgradeBanner user={user} keys={keys} />
                    <div className="space-y-3" data-testid="dev-keys-list">
                        {keys.map((k) => (
                            <KeyRow key={k.key_id} k={k} onRevoke={() => revoke(k)} />
                        ))}
                    </div>
                </>
            )}

            {/* PLAN QUOTA CARD */}
            <Card className="card-surface rounded-2xl p-6 mt-10 border border-primary/20" data-testid="dev-plan-card">
                <div className="flex items-start gap-4">
                    <Sparkles className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <h3 className="font-display text-xl text-accent">
                            Your plan: {user?.is_pro ? "Real-Check Pro" : "Free"}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            {user?.is_pro
                                ? "Every key you create gets 100,000 requests/day — effectively unlimited."
                                : "Each key gets 1,000 requests/day. Upgrade to Pro for effectively unlimited quota."}
                        </p>
                        {!user?.is_pro && (
                            <Link to="/pricing">
                                <Button className="rounded-full mt-4 bg-primary hover:bg-primary/90 text-primary-foreground">
                                    Upgrade for higher quota →
                                </Button>
                            </Link>
                        )}
                    </div>
                </div>
            </Card>

            {/* CREATE MODAL */}
            <AnimatePresence>
                {showCreate && (
                    <CreateKeyModal
                        onClose={() => setShowCreate(false)}
                        onCreated={(data) => {
                            setNewlyCreated(data);
                            setShowCreate(false);
                            loadKeys();
                        }}
                    />
                )}
            </AnimatePresence>

            {/* REVEAL NEW KEY MODAL */}
            <AnimatePresence>
                {newlyCreated && (
                    <RevealKeyModal
                        data={newlyCreated}
                        onClose={() => setNewlyCreated(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

// -----------------------------------------------------------
// QuotaUpgradeBanner — shows only for Free users when at least
// one key is at ≥80% of daily quota. Clicking upgrade takes
// them straight to Stripe checkout via the pricing page.
// -----------------------------------------------------------
function QuotaUpgradeBanner({ user, keys }) {
    if (!user || user.is_pro) return null;
    const hottest = keys
        .filter((k) => k.is_active)
        .map((k) => ({ ...k, pct: (k.used_today / (k.daily_quota || 1)) * 100 }))
        .sort((a, b) => b.pct - a.pct)[0];
    if (!hottest || hottest.pct < 80) return null;
    const nearMax = hottest.pct >= 100;

    return (
        <Card
            className={`mb-4 rounded-2xl p-5 relative overflow-hidden border ${nearMax ? "border-red-400/40 bg-red-500/5" : "border-amber-400/40 bg-amber-500/5"}`}
            data-testid="dev-upgrade-banner"
        >
            <div className={`absolute -top-8 -right-8 w-40 h-40 rounded-full blur-3xl ${nearMax ? "bg-red-500/20" : "bg-amber-400/20"} pointer-events-none`} />
            <div className="relative flex flex-wrap items-center gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${nearMax ? "bg-red-500/20 border border-red-400/40" : "bg-amber-400/20 border border-amber-400/40"}`}>
                    {nearMax ? (
                        <TrendingUp className="h-6 w-6 text-red-300" />
                    ) : (
                        <Rocket className="h-6 w-6 text-amber-300" />
                    )}
                </div>
                <div className="flex-1 min-w-[220px]">
                    <h3 className="font-display text-lg text-accent" data-testid="dev-upgrade-title">
                        {nearMax
                            ? "You've hit your daily API cap"
                            : `${Math.round(hottest.pct)}% of today's quota used`}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        {nearMax ? (
                            <>Your key <span className="text-accent font-semibold">{hottest.name}</span> just maxed out {hottest.daily_quota.toLocaleString()} requests. Pro unlocks 100,000/day.</>
                        ) : (
                            <>Your key <span className="text-accent font-semibold">{hottest.name}</span> is close to its daily ceiling. Upgrade before it locks you out.</>
                        )}
                    </p>
                </div>
                <Link to="/pricing?intent=api" className="shrink-0">
                    <Button
                        className={`rounded-full h-11 px-5 font-semibold ${nearMax ? "bg-red-500 hover:bg-red-500/90 text-white" : "bg-amber-400 hover:bg-amber-400/90 text-black"}`}
                        data-testid="dev-upgrade-cta"
                    >
                        Upgrade to Pro · $9/mo
                    </Button>
                </Link>
            </div>
        </Card>
    );
}


function KeyRow({ k, onRevoke }) {    const usedPct = Math.min(100, Math.round((k.used_today / (k.daily_quota || 1)) * 100));
    return (
        <Card className="card-surface rounded-2xl p-5" data-testid={`dev-key-${k.key_id}`}>
            <div className="flex flex-wrap items-start gap-4">
                <div className="flex-1 min-w-[250px]">
                    <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-display text-lg text-accent">{k.name}</h4>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${k.plan === "pro" ? "border-amber-400/40 bg-amber-400/15 text-amber-300" : "border-white/10 text-muted-foreground"}`}>
                            {k.plan}
                        </span>
                        {!k.is_active && (
                            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-red-400/40 bg-red-400/15 text-red-300">
                                revoked
                            </span>
                        )}
                    </div>
                    <div className="font-mono text-sm text-muted-foreground select-all">
                        {k.prefix}<span className="opacity-40">••••••••••••••••••••••••</span>
                    </div>
                    <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                        <span>
                            <strong className="text-accent tabular-nums">{k.used_today}</strong> / {k.daily_quota.toLocaleString()} today
                        </span>
                        <span className="text-muted-foreground/50">·</span>
                        <span><strong className="text-accent tabular-nums">{k.total_requests}</strong> lifetime</span>
                        <span className="text-muted-foreground/50">·</span>
                        <span>created {new Date(k.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="mt-2 h-1.5 w-full rounded-full bg-white/5 overflow-hidden max-w-md">
                        <div className="h-full bg-gradient-to-r from-primary to-emerald-400" style={{ width: `${usedPct}%` }} />
                    </div>
                </div>
                {k.is_active && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-300 hover:text-red-200 hover:bg-red-500/10 rounded-full"
                        onClick={onRevoke}
                        data-testid={`dev-revoke-${k.key_id}`}
                    >
                        <Trash2 className="h-4 w-4 mr-1" /> Revoke
                    </Button>
                )}
            </div>
        </Card>
    );
}

function CreateKeyModal({ onClose, onCreated }) {
    const [name, setName] = useState("");
    const [creating, setCreating] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (!name.trim()) return toast.error("Name required");
        setCreating(true);
        try {
            const { data } = await api.post("/keys", { name: name.trim() });
            onCreated(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create key");
        } finally {
            setCreating(false);
        }
    }

    return (
        <motion.div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="dev-create-modal"
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                onClick={(e) => e.stopPropagation()}
                className="card-surface rounded-2xl p-7 w-full max-w-md border border-primary/20"
            >
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-2xl text-accent">Create a new key</h3>
                    <button onClick={onClose} className="text-muted-foreground hover:text-accent"><X className="h-5 w-5" /></button>
                </div>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="text-xs uppercase tracking-widest text-muted-foreground">Key name</label>
                        <Input
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Chrome extension prod"
                            className="mt-2 bg-white/5 border-white/10"
                            data-testid="dev-create-name-input"
                        />
                        <p className="text-xs text-muted-foreground mt-2">Just a label — helps you remember what this key is for.</p>
                    </div>
                    <Button
                        type="submit"
                        disabled={creating}
                        className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
                        data-testid="dev-create-submit"
                    >
                        {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</> : "Create key"}
                    </Button>
                </form>
            </motion.div>
        </motion.div>
    );
}

function RevealKeyModal({ data, onClose }) {
    const [copied, setCopied] = useState(false);
    async function copyKey() {
        try {
            await navigator.clipboard.writeText(data.full_key);
            setCopied(true);
            toast.success("Key copied to clipboard");
            setTimeout(() => setCopied(false), 2500);
        } catch (e) {
            toast.error("Could not copy — select and copy manually");
        }
    }

    return (
        <motion.div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="dev-reveal-modal"
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="card-surface rounded-2xl p-8 w-full max-w-lg border border-amber-400/40 relative"
            >
                <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert className="h-5 w-5 text-amber-300" />
                    <h3 className="font-display text-2xl text-accent">Copy your key now</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-5">
                    This is the only time we&#39;ll show <strong className="text-amber-300">{data.name}</strong> in full.
                    Store it in a password manager, environment variable, or your extension&#39;s secrets.
                </p>
                <div
                    className="bg-black/60 border border-white/10 rounded-xl p-4 font-mono text-sm text-accent break-all select-all cursor-pointer relative group"
                    onClick={copyKey}
                    data-testid="dev-reveal-key-value"
                >
                    {data.full_key}
                    <button
                        onClick={copyKey}
                        className="absolute top-3 right-3 p-2 rounded-full bg-white/5 hover:bg-white/10 transition"
                        aria-label="Copy"
                        data-testid="dev-reveal-copy-btn"
                    >
                        {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4 text-muted-foreground" />}
                    </button>
                </div>
                <div className="mt-5 text-xs text-muted-foreground space-y-1">
                    <div>Plan: <span className="text-accent">{data.plan}</span></div>
                    <div>Daily quota: <span className="text-accent">{data.daily_quota.toLocaleString()} requests</span></div>
                </div>
                <div className="mt-6 flex gap-2 justify-end">
                    <Link to="/docs/api">
                        <Button variant="secondary" className="rounded-full">View docs & extension sample →</Button>
                    </Link>
                    <Button onClick={onClose} className="rounded-full bg-primary text-primary-foreground" data-testid="dev-reveal-done-btn">
                        I&#39;ve saved it
                    </Button>
                </div>
            </motion.div>
        </motion.div>
    );
}
